// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * Types for the resource-server tag taxonomy + assignments (Tags PRD
 * §7.1/§8.1-8.2). `FUND` is intentionally NOT part of this union — fund
 * assignments are federated to funds-service and never write an
 * `entity_tags` row; see `../api/fundsApi.ts` for the fund-side types.
 */
export type TaggableEntityType = 'RELATIONSHIP' | 'CLIENT' | 'POLICY' | 'DOCUMENT';

/** Mirrors {@code com.loa.dto.tag.TagDTO}. */
export interface TagDTO {
  id: string;
  name: string;
  color: string;
  description?: string | null;
  archived: boolean;
  /** Total assignment count across all entity types — only populated when withUsage=true. */
  usageCount?: number | null;
  createdBy?: string | null;
  createdAt?: string;
  updatedBy?: string | null;
  updatedAt?: string;
}

/** Mirrors {@code com.loa.dto.tag.CreateTagRequest}. */
export interface CreateTagRequest {
  name: string;
  color?: string;
  description?: string;
}

/** Mirrors {@code com.loa.dto.tag.UpdateTagRequest} — every field optional. */
export interface UpdateTagRequest {
  name?: string;
  color?: string;
  description?: string;
  archived?: boolean;
}

/** Mirrors {@code com.loa.dto.tag.TagUsageDTO}. */
export interface TagUsageDTO {
  tagId: string;
  totalCount: number;
  countsByEntityType: Record<string, number>;
}

/** Mirrors {@code com.loa.dto.tag.MergeTagResultDTO}. */
export interface MergeTagResultDTO {
  sourceTagId: string;
  targetTagId: string;
  movedCount: number;
  collapsedCount: number;
}

/** Mirrors {@code com.loa.dto.tag.EntityTagsDTO}. */
export interface EntityTagsDTO {
  entityType: string;
  entityId: string;
  tags: TagDTO[];
}

/** 12-swatch palette tokens — mirrors {@code com.loa.service.tag.TagPalette.TOKENS}. */
export const TAG_PALETTE_TOKENS = [
  'slate', 'red', 'orange', 'amber', 'emerald', 'teal',
  'sky', 'blue', 'indigo', 'violet', 'fuchsia', 'rose',
] as const;

export type TagPaletteToken = typeof TAG_PALETTE_TOKENS[number];
